import { CheckCircle2, Calendar, Inbox, Clock, Sparkles, Star } from 'lucide-react';
import { useEffect, useState } from 'react';

// Motivational messages for celebration
const CELEBRATION_MESSAGES = [
  "Great leadership day! Rest well.",
  "Tomorrow is set. Time to recharge!",
  "Well done! Your future self will thank you.",
  "Planning complete. You're ready to lead!",
  "Inbox cleared, tomorrow planned. Victory!",
];

export default function PlanningComplete({
  stats = {},
  onDone
}) {
  const {
    inboxProcessed = 0,
    tasksScheduled = 0,
    blocksUsed = 0,
    totalBlocks = 0
  } = stats;

  const [showConfetti, setShowConfetti] = useState(false);
  const [message] = useState(() =>
    CELEBRATION_MESSAGES[Math.floor(Math.random() * CELEBRATION_MESSAGES.length)]
  );

  // Trigger confetti animation on mount
  useEffect(() => {
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center relative overflow-hidden">
      {/* Celebration particles */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-[confetti_3s_ease-out_forwards]"
              style={{
                left: `${10 + (i * 7)}%`,
                top: '-20px',
                animationDelay: `${i * 0.1}s`,
              }}
            >
              {i % 3 === 0 ? (
                <Star size={16} className="text-accent" fill="currentColor" />
              ) : i % 3 === 1 ? (
                <Sparkles size={16} className="text-weekly" />
              ) : (
                <Star size={12} className="text-success" fill="currentColor" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Success icon with pulse animation */}
      <div className="w-24 h-24 rounded-full bg-success/10 flex items-center justify-center mb-6 animate-in opacity-0 delay-1" style={{ animationFillMode: 'forwards' }}>
        <div className="w-20 h-20 rounded-full bg-success/20 flex items-center justify-center animate-pulse">
          <CheckCircle2 size={48} className="text-success" />
        </div>
      </div>

      {/* Title */}
      <h2 className="text-2xl font-bold text-text-primary mb-2 animate-in opacity-0 delay-2" style={{ animationFillMode: 'forwards' }}>
        Tomorrow is Ready!
      </h2>
      <p className="text-sm text-text-tertiary mb-2 max-w-xs animate-in opacity-0 delay-3" style={{ animationFillMode: 'forwards' }}>
        You've successfully planned your day.
      </p>
      <p className="text-sm font-medium text-accent mb-8 animate-in opacity-0 delay-3" style={{ animationFillMode: 'forwards' }}>
        {message}
      </p>

      {/* Stats with staggered animations */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-md mb-8">
        {inboxProcessed > 0 && (
          <div className="p-4 bg-surface-card rounded-xl border border-border shadow-[var(--shadow-soft)] animate-in opacity-0 delay-4 hover:shadow-[var(--shadow-card)] transition-shadow" style={{ animationFillMode: 'forwards' }}>
            <div className="w-10 h-10 rounded-lg bg-accent-muted flex items-center justify-center mx-auto mb-2">
              <Inbox size={20} className="text-accent" />
            </div>
            <p className="text-2xl font-bold text-text-primary">{inboxProcessed}</p>
            <p className="text-xs text-text-tertiary">Items processed</p>
          </div>
        )}

        <div className="p-4 bg-surface-card rounded-xl border border-border shadow-[var(--shadow-soft)] animate-in opacity-0 delay-5 hover:shadow-[var(--shadow-card)] transition-shadow" style={{ animationFillMode: 'forwards' }}>
          <div className="w-10 h-10 rounded-lg bg-weekly-light flex items-center justify-center mx-auto mb-2">
            <Calendar size={20} className="text-weekly" />
          </div>
          <p className="text-2xl font-bold text-text-primary">{tasksScheduled}</p>
          <p className="text-xs text-text-tertiary">Tasks scheduled</p>
        </div>

        <div className="p-4 bg-surface-card rounded-xl border border-border shadow-[var(--shadow-soft)] animate-in opacity-0 delay-6 hover:shadow-[var(--shadow-card)] transition-shadow" style={{ animationFillMode: 'forwards' }}>
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mx-auto mb-2">
            <Clock size={20} className="text-amber-500" />
          </div>
          <p className="text-2xl font-bold text-text-primary">
            {blocksUsed}<span className="text-text-tertiary text-lg">/{totalBlocks}</span>
          </p>
          <p className="text-xs text-text-tertiary">Blocks filled</p>
        </div>
      </div>

      {/* Done button */}
      <button
        onClick={onDone}
        className="px-8 py-3 bg-gradient-to-r from-accent to-orange-hover text-white rounded-xl text-sm font-semibold border-none cursor-pointer shadow-lg shadow-accent/20 hover:shadow-accent/30 hover:scale-[1.02] active:scale-[0.98] transition-all animate-in opacity-0 delay-6"
        style={{ animationFillMode: 'forwards' }}
      >
        Done for Today
      </button>
    </div>
  );
}
